import type { ApiPromise } from "@polkadot/api"
import type { Option, bool, u32 } from "@polkadot/types"
import type { QueryFunctionContext } from "@tanstack/query-core"
import type { RarityLiterval, RaceLiterval } from "@/types/nftAttribute"
import type { Path } from 'ramda'

import * as R from 'ramda'

import { getPreorderList, getPreorderCounts } from "./requests"

//
//
//

export type NftSaleInfo = {
  raceCount: number;
  raceForSaleCount: number;
  raceGiveawayCount: number;
  raceReservedCount: number;
};

//
//
//

export function queryPreorderList(prefix: string, address: string) {
  return {
    queryKey: ['preorderList', prefix, address],
    queryFn: async (ctx: any) => {
      const { queryKey: [, prefix, address] } = ctx as QueryFunctionContext<[string, string, string]>
      const result = await getPreorderList(prefix, address)
      return result
    },
    staleTime: 1_000 * 60 * 5, // 5 mins
  }
}

export function queryOwnerHasPreorder(api: ApiPromise, address: string) {
  return {
    queryKey: ['ownerHasPreorder', address],
    queryFn: async (ctx: any) => {
      const { queryKey: [, address] } = ctx as QueryFunctionContext<[string, string]>
      const result = await api.query.pwNftSale.ownerHasPreorder(address)
      return result.toJSON()
    },
  }
}

export function queryPreorderIndex(api: ApiPromise) {
  return {
    queryKey: ['preorderIndex'],
    queryFn: async (ctx: any) => {
      const result = await api.query.pwNftSale.preorderIndex()
      return result.toJSON()
    },
  }
}

export function queryPreorderCountsByRace(prefix: string) {
  return {
    queryKey: ['preorderCountsByRace', prefix],
    queryFn: async (ctx: any) => {
      const { queryKey: [, prefix] } = ctx as QueryFunctionContext<[string, string]>
      const result = await getPreorderCounts(prefix)
      return result
    }
  }
}

export function querySaleContext(api: ApiPromise) {
  return {
    query: ['querySaleContext'],
    queryFn: async () => {
      const [[
        originOfShellCollectionId,
        spiritCollectionId,
        shellCollectionId,
        canPreorderOriginOfShells,
        canPurchaseRareOriginOfShells,
        canPurchasePrimeOriginOfShells,
        lastDayOfSale,
      ], ...inventoryQuery] = await Promise.all([
        api.queryMulti<[
          Option<u32>, Option<u32>, Option<u32>, bool, bool, bool, bool,
        ]>([
          api.query.pwNftSale.originOfShellCollectionId,
          api.query.pwNftSale.spiritCollectionId,
          api.query.pwIncubation.shellCollectionId,
          api.query.pwNftSale.canPreorderOriginOfShells,
          api.query.pwNftSale.canPurchaseRareOriginOfShells,
          api.query.pwNftSale.canPurchasePrimeOriginOfShells,
          api.query.pwNftSale.lastDayOfSale,
        ]),
        api.query.pwNftSale.originOfShellsInventory.entries('Prime'),
        api.query.pwNftSale.originOfShellsInventory.entries('Magic'),
        api.query.pwNftSale.originOfShellsInventory.entries('Legendary')
      ]);
      const inventoryLevel = R.mergeAll(
        R.map(
          R.reduce(
            (acc, i) => R.assocPath(i[0]?.toHuman() as Path, i[1]?.toJSON(), acc),
            {} as Record<RarityLiterval, Record<RaceLiterval, NftSaleInfo>>
          ),
          inventoryQuery
        )
      );
      return {
        originOfShellCollectionId: originOfShellCollectionId.unwrap().toNumber(),
        spiritCollectionId: spiritCollectionId.unwrap().toNumber(),
        shellCollectionId: shellCollectionId.unwrap().toNumber(),
        canPreorderOriginOfShells: canPreorderOriginOfShells.toPrimitive(),
        canPurchaseRareOriginOfShells: canPurchaseRareOriginOfShells.toPrimitive(),
        canPurchasePrimeOriginOfShells: canPurchasePrimeOriginOfShells.toPrimitive(),
        lastDayOfSale: lastDayOfSale.toPrimitive(),
        inventoryLevel,
      }
    }
  }
}